// Walking about inside the gated lobby: WASD steers, the camera stays on the plane it
// opened at, and the gate is a wall. Pure geometry — no three.js import, so it is
// testable in jsdom. The listeners and the per-frame call live in
// 3DWebpage/LobbyWalkControls.js.

/**
 * Units per second. The gate is only five units in front of the camera, so this crosses
 * the whole lobby in about two seconds. It is nothing like the `movementSpeed = 100`
 * set in Initial3DSetup.js — that is a FlyControls property, which OrbitControls has no
 * use for and ignores.
 */
export const WALK_SPEED = 3;

/**
 * The leash, measured from the gate rather than the camera, so it stays put if the
 * lobby moves: the walk may close to within a unit and a half of the doors, back off
 * twelve units — the camera starts five in, so seven behind it — and step two units
 * either side.
 *
 * The front limit is kept above the camera's one-unit near plane (Initial3DSetup.js), so
 * the closest the walk can get still leaves the gate in front of it and drawn.
 */
export const LEASH_FRONT = 1.5;
export const LEASH_BACK = 12;
export const LEASH_LATERAL = 2;

/** What each steering key asks for, as [right, forward]. */
const KEYS = {
    keyw: [0, 1],
    keys: [0, -1],
    keyd: [1, 0],
    keya: [-1, 0],
};

const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

/**
 * Where the camera is trying to walk this frame. `keys` is whatever the wiring is
 * holding — event.code strings, so 'KeyW', matched without regard to case — and
 * `facing` is the direction the camera looks, which need not be level.
 *
 * Returns a world-space step, never a position: the camera decides where it is.
 */
export function walkDelta(keys, facing, seconds, speed = WALK_SPEED) {
    let right = 0;
    let forward = 0;

    Array.from(keys).forEach((key) => {
        const steering = KEYS[String(key).toLowerCase()];

        if (!steering) return;

        right += steering[0];
        forward += steering[1];
    });

    if (right === 0 && forward === 0) return { x: 0, y: 0, z: 0 };

    // Walking is flat, so only the level part of the facing is anything to go on.
    // Looking straight up leaves nothing of it, and there is no sensible way to walk
    // towards the sky — fall back to straight ahead rather than dividing by zero.
    const length = Math.hypot(facing.x, facing.z);
    const fx = length > 0 ? facing.x / length : 0;
    const fz = length > 0 ? facing.z / length : -1;

    // The camera's right hand, level: forward crossed with up (+Y). For a camera facing
    // -Z that is +X, which is the direction it will strafe on D.
    const x = fx * forward + -fz * right;
    const z = fz * forward + fx * right;
    const scale = (speed * seconds) / Math.hypot(x, z);

    return { x: x * scale, y: 0, z: z * scale };
}

/**
 * Holds the camera inside the lobby. The gate's own side of it is +Z — the camera opens
 * on z = 10000 and the gate is five units along -Z — so walking towards the doors is a
 * falling z, and the near limit keeps a unit of clearance rather than clipping through.
 * Height is not the walk's to move, so it is handed back as it came.
 */
export function clampToGate(position, gate) {
    return {
        x: clamp(position.x, gate.x - LEASH_LATERAL, gate.x + LEASH_LATERAL),
        y: position.y,
        z: clamp(position.z, gate.z + LEASH_FRONT, gate.z + LEASH_BACK),
    };
}
